import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, DollarSign, BarChart, Play, MousePointer, PieChart } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
}

const AdvertisingMediaPerformancePage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/advertising-media-performance`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Advertising & Media Performance Calculators | Free Marketing Tools</title>
        <meta name="description" content="Calculate advertising ROI, ROAS, share of voice & more with our free calculators. Optimize your advertising and media performance." />
        <meta name="keywords" content="advertising ROI calculator, ROAS calculator, share of voice, video view value, banner ad CTR" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Advertising & Media Performance Calculators" />
        <meta property="og:description" content="Calculate and improve your advertising performance with our free calculators. Measure ROI, ROAS, and more." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Advertising & Media Performance Calculators" />
        <meta property="twitter:description" content="Calculate and improve your advertising performance with our free calculators. Measure ROI, ROAS, and more." />
        <meta property="twitter:image" content={logoUrl} />
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Advertising & Media Performance</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Advertising & Media Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard 
            title="Advertising ROI" 
            description="Calculate the return on investment for your advertising campaigns" 
            link="/calculators/advertising-roi" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Return on Ad Spend (ROAS)" 
            description="Measure the revenue generated for every dollar spent on advertising" 
            link="/calculators/return-on-ad-spend" 
            icon={DollarSign}
          />
          <CalculatorCard 
            title="Share of Voice" 
            description="Calculate your brand's share of advertising within its product category" 
            link="/calculators/share-of-voice" 
            icon={BarChart}
          />
          <CalculatorCard 
            title="YouTube View Time Value" 
            description="Calculate the value of view time on YouTube" 
            link="/calculators/youtube-view-time-value" 
            icon={Play}
          />
          <CalculatorCard 
            title="Cost Per Video View" 
            description="Measure the cost-effectiveness of your video advertising" 
            link="/calculators/cost-per-video-view" 
            icon={DollarSign}
          />
          <CalculatorCard 
            title="Banner Ad Click-Through Rate" 
            description="Calculate the effectiveness of your banner ads" 
            link="/calculators/banner-ad-click-through-rate" 
            icon={MousePointer}
          />
          <CalculatorCard 
            title="Effective Cost Per Mille" 
            description="Calculate the effective cost per thousand ad impressions" 
            link="/calculators/effective-cost-per-mille" 
            icon={PieChart}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Understanding Advertising & Media Performance</h2>
        <p className="text-gray-600 mb-4">
          Measuring advertising and media performance helps you optimize your campaigns and maximize your return on investment. Use these calculators to track and improve your advertising effectiveness.
        </p>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link, icon: Icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <Icon className="w-8 h-8 text-indigo-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default AdvertisingMediaPerformancePage;
